import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { getJSON } from '@/lib/api'

interface StoredEmail {
  subject: string | null
  rendered_body: string | null
}

export function EmailLogDetailSheet({
  id,
  scope,
  onClose,
}: {
  id: number | null
  scope: string
  onClose: () => void
}) {
  return (
    <Sheet
      open={id !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      {id !== null ? <StoredEmailContent key={`${scope}:${id}`} id={id} scope={scope} /> : null}
    </Sheet>
  )
}

function StoredEmailContent({ id, scope }: { id: number; scope: string }) {
  const query = useQuery({
    queryKey: ['email-sends', 'detail', scope, id],
    gcTime: 0,
    queryFn: () => getJSON<{ email_send: StoredEmail }>(`/api/v1/email_sends/${id}`),
    retry: false,
  })
  const email = query.isError ? undefined : query.data?.email_send
  return (
    <SheetContent className="sm:max-w-2xl">
      <SheetHeader>
        <SheetTitle>{email?.subject ?? 'Email details'}</SheetTitle>
        <SheetDescription>The message captured when this email was prepared.</SheetDescription>
      </SheetHeader>
      {query.isPending ? <p role="status">Loading email</p> : null}
      {query.isError ? (
        <Alert>
          <p>Email unavailable</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {email ? <StoredBody body={email.rendered_body} /> : null}
    </SheetContent>
  )
}

function StoredBody({ body }: { body: string | null }) {
  if (!body?.trim()) return <p>This email body is no longer retained.</p>
  const policy =
    "default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'"
  return (
    <iframe
      title="Stored email body"
      sandbox=""
      referrerPolicy="no-referrer"
      className="min-h-[60vh] w-full rounded-md border border-border bg-background"
      srcDoc={`<!doctype html><meta http-equiv="Content-Security-Policy" content="${policy}">${body}`}
    />
  )
}
